// @LTG-ID: data/journals/selling-a-retail-business
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0108
// @CHANGED: 2026-07-06 (CHG-0108 · Journal port completion, articles 21-40)
// @OWNER: web
//
// Article 35 of 40. Source: batch MD at /app/_ltg_content_batches/batch_3_4/.
// Byline: LTG Team (per CHG-0107.6 §17.3 convention).
// Section: Industry-Specific (per CHG-0108 D4 orchestrator adjudication).

export const BRIEF = {
    SLUG: "selling-a-retail-business",
    FRONTMATTER: {
        number: "72",
        section: "Industry-Specific",
        sectionHref: "/journals#industry-specific",
        sectionAnchor: "industry-specific",
        title: "Selling a Retail Business: Inventory, Location, and Loyalty in the Buyer's Eyes",
        deck: "Retail businesses sell on provable margins, location strength, and customer loyalty that transfers.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-06-26",
        publishedHuman: "June 26, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 461,
        timeRequired: "PT3M",
        keywords: "selling a retail business, retail store valuation, selling a store, retail business sale inventory",
        articleSection: "Industry-Specific",
        articleBodyPreview: "Retail businesses sell on provable margins, location strength, and customer loyalty that transfers. Inventory is usually priced separately at cost on top of the business price, verified by a physical count near closing. Stale inventory, weak lease terms, and dependence on the owner's personal customer relationships are the recurring discounts. Retail selling has its own grammar, starting with a convention that surprises first time sellers: the price is usually two numbers, not one. The inventory",
        complianceNote: "This article is educational and does not constitute legal or financial advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "recurring-revenue-business-value",
        "clean-financial-records-business-value",
        "selling-a-restaurant"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Selling a Retail Business: Inventory, Location, Loyalty",
        canonical: "https://letstalkgrowth.com/journals/selling-a-retail-business",
        ogImage: "/journals/og/selling-a-retail-business.jpg",
    },
    BODY_MARKDOWN: `Retail businesses sell on provable margins, location strength, and customer loyalty that transfers. Inventory is usually priced separately at cost on top of the business price, verified by a physical count near closing. Stale inventory, weak lease terms, and dependence on the owner's personal customer relationships are the recurring discounts.

Retail selling has its own grammar, starting with a convention that surprises first-time sellers: the price is usually two numbers, not one.

## The inventory convention

Common market practice prices a retail business as its operating value plus inventory, with inventory typically transferred at the seller's cost, verified by a physical count at or near closing, and often subject to negotiated exclusions. Those exclusions are where preparation pays: stale, damaged, and unsellable stock gets carved out or discounted, and a store carrying years of accumulated dead inventory funds an unpleasant surprise at the count. The pre-sale discipline is obvious once stated: clean the inventory position early, mark down and move the dead stock on your own schedule rather than the buyer's, and keep records that make the count boring. Boring counts close deals.

## Location, lease, and the traffic question

Like restaurants, most retailers rent their most important asset. Everything in our restaurant article about lease term, assignability, and landlord dynamics applies here unchanged, and deserves the same early attention. Layered on top is the traffic question every retail buyer asks: why do customers come, and will they keep coming under new ownership? A store whose draw is location, category, and merchandising transfers well. A store whose draw is the owner personally, the proprietor customers come to see, carries the same owner-dependence discount this series describes everywhere else, wearing a name tag.

## The channel story

Buyers increasingly evaluate a retailer's online dimension: whether e-commerce, even modest, complements the physical store, whether customer data exists in usable form, a mailing list, a loyalty program, purchase histories in the POS, and whether the store's social and review presence is an owned asset that conveys. None of this requires being a technology business; it requires that whatever customer relationships exist are institutional and transferable rather than resident in the owner's personal accounts. A documented, consented customer list is retail's closest cousin to recurring revenue, and buyers price it that way.

## What the prepared retail seller looks like

Margins provable from clean POS and accounting records; an inventory position that is current, counted, and honest; a lease with workable term and assignment; customer relationships held by the store rather than the owner; and staff who can run the floor without the proprietor present. It is the universal checklist from this series, translated into shelf and register, and the retailers who work it meet a market of buyers rather than a market of skeptics.`,
    FAQ: [
        { question: "Is inventory included in a retail business's sale price?", answer: "Usually it is priced separately, transferred at cost on top of the business price and verified by a physical count near closing, with unsellable stock excluded or discounted. Cleaning up inventory before a sale prevents value leaking at the count." },
        { question: "What makes a retail store attractive to buyers?", answer: "Provable margins, a strong and assignable lease, current inventory, transferable customer relationships such as loyalty programs and mailing lists, and operations that run without the owner on the floor." },
        { question: "Does my store need e-commerce to sell?", answer: "No, but transferable customer assets help: a POS with purchase history, a consented email list, owned social and review profiles. Buyers pay for customer relationships the business owns rather than ones the proprietor personally holds." }
    ],
    SOURCES: [
    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal \u2192",
        microLine: "\u00b7 7-DAY DELIVERY \u00b7 KGOB METHODOLOGY, NC CPA #30420 \u00b7",
    },
    READ_NEXT: [
        {
            tag: "Value Drivers",
            title: "Recurring Revenue: Why Buyers Pay More for Businesses That Bill Every Month",
            meta: "3 min · Brief 49",
            to: "/journals/recurring-revenue-business-value",
        },
        {
            tag: "Value Drivers",
            title: "Why Clean Financial Records Are Worth Real Money When You Sell",
            meta: "3 min · Brief 51",
            to: "/journals/clean-financial-records-business-value",
        },
        {
            tag: "Industry-Specific",
            title: "Selling a Restaurant: The Honest Guide for Independent and Franchise Owners",
            meta: "3 min · Brief 71",
            to: "/journals/selling-a-restaurant",
        },
    ],
};

export default BRIEF;
